<template>
  <main role="main" class="py-3">
    <b-container class="container-x">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-3 border-bottom pb-2">
        <h5 class="m-0 text-dark font-weight-bold">{{ $t("subPlanList") }} ({{subscriptionPlans.length}})</h5>
        <div class="d-flex flex-row align-items-center">
          <b-form-radio-group v-model="selectedTab" :options="tabOptions" buttons button-variant="outline-primary" size="sm" @change="onSelectTab" class="mr-2"/>
          <b-button variant="primary" size="sm" class="d-flex align-items-center px-3 btn-create-custom" to="/subscription-plans/new" v-if="isUserAdmin()">
            <b-icon icon="plus" class="mr-1"></b-icon>
            {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5"><b-spinner type="grow" variant="primary"/></div>

      <!-- Plan List -->
      <div v-if="subscriptionPlans && subscriptionPlans.length" class="policy-list">
        <b-card v-for="plan in subscriptionPlans" :key="plan.id" class="policy-card border mb-3" :class="{ 'text-secondary': !plan.active }" no-body>
          <div class="card-header d-flex justify-content-between align-items-center bg-transparent border-bottom-0 pt-3 px-3 pb-0">
            <h6 class="m-0 font-weight-bold text-dark policy-title">
              {{ plan.name }}
              <span class="plan-subtitle ml-2 text-muted">
                | {{ formatCurrencyAmount(plan.price) }} VND / {{ plan.numberOfDaysUsage }} {{ $t(plan.unitTime+'s') }}
                {{ getStorageLabel(plan) }}
                {{ getQuotaAILabel(plan) }}
              </span>
            </h6>
            <div class="d-flex align-items-center">
              <b-dropdown v-if="isUserAdmin()" variant="link" toggle-class="text-decoration-none p-0" no-caret right>
                <template #button-content>
                  <b-icon icon="chevron-down" class="text-secondary" font-scale="0.9"></b-icon>
                </template>
                <b-dropdown-item :to="'/subscription-plans/edit/' + plan.id">{{ $t("update") }}</b-dropdown-item>
                <b-dropdown-item-button @click="onClickDeletePlan(plan)" variant="danger">{{ $t("actionDelete") }}</b-dropdown-item-button>
              </b-dropdown>
            </div>
          </div>

          <b-card-body class="px-3 pb-3 pt-2">
            <b-row>
              <!-- Left Column: Plan Details -->
              <b-col md="6" class="border-right-custom">
                <ul class="plan-info-list list-unstyled m-0">
                  <li v-for="(item, index) in getPlanItems(plan)" :key="index" class="d-flex align-items-center">
                    <span class="indicator-dot mr-2"></span>
                    <span class="info-value text-dark">{{ $t(item.key, { count: item.value }) }}</span>
                  </li>
                </ul>
              </b-col>

              <!-- Right Column: Features List -->
              <b-col md="6" class="pl-md-3 mt-2 mt-md-0">
                <div class="text-secondary small mb-1">{{ $t("featureList") || "Tính năng kích hoạt" }}:</div>
                <div v-for="feature in getActiveFeatures(plan).slice(0, 5)" :key="feature.featureId" class="d-flex align-items-center mb-1">
                  <b-icon icon="check2" class="mr-2 primary-color" font-scale="0.95"/>
                  <span class="text-break small text-dark">{{ feature.featureName }}</span>
                </div>

                <div v-if="getActiveFeatures(plan).length > 5" class="d-flex mt-2">
                  <a class="text-link small" style="cursor: pointer;"
                     v-b-tooltip.bottom.hover.html="{ title : getFeatureTooltip(plan), customClass: 'tooltip-class'}">
                    {{$t("viewmore")}} 
                    <i class="fa fa-angle-right" style="font-size: 12px" aria-hidden="true"></i>
                  </a>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>

      <div v-if="subscriptionPlans && !subscriptionPlans.length" class="text-center py-5">
        <span class="text-secondary small">{{ $t("noData") }}</span>
      </div>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 1000px;
}

.policy-card {
  border-radius: 8px;
  background-color: #f4f9ff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.policy-title {
  font-size: 1.1rem;
  letter-spacing: -0.01em;
}

.plan-subtitle {
  font-size: 0.85rem;
  font-weight: 500;
}

.plan-info-list {
  li {
    margin-bottom: 6px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.indicator-dot {
  width: 5px;
  height: 5px;
  background-color: #8a94a6;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.info-label {
  min-width: 170px;
  display: inline-block;
}

.info-value {
  font-size: 0.9rem;
}

.primary-color {
  color: #8a94a6;
}

.text-link {
  color: #6c757d;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
}

.font-weight-bold {
  font-weight: 600 !important;
}

@media (min-width: 768px) {
  .border-right-custom {
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
}
</style>

<style lang="scss">
.tooltip .tooltip-inner {
  max-width: 400px !important;
}
.tooltip-inner {
  background-color: #2b2b2b !important;
  color: #fff !important;
  padding: 10px 12px !important;   
  border-radius: 6px !important;
  font-size: 13px !important;
  text-align: left !important;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
}
.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #2d2d2d !important;
}
.tooltip-inner br {
  display: block;
  margin-bottom: 8px;
}
</style>

<script lang="ts">
import {Component} from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import { Status } from '../enums/EnableStatus';
import {bytesToGB} from "@/service/util";
import { StorageType } from '../enums/storageType';
import { ManagementType } from '../enums/subscriptionAction';
import * as util from "@/service/util";
import * as subscriptionUtil from "@/service/subscriptionUtil";

@Component
export default class SubscriptionPlans extends BaseComponent {

  enumManagementType = ManagementType;
  enumStatus = Status;

  getPlanItems(plan) {
    return subscriptionUtil.buildCustomerPackageItems(plan, plan);
  }
  tabOptions = [
    { value: true, text: this.$t("activePackage").toString() },
    { value: false, text: this.$t("unactivePackage").toString() },
  ];
  selectedTab = true;
  isLoading = false;
  subscriptionPlans: any[] = [];
  featureList = [];

  mounted() {
    this.fetchPlans();
    //this.onSelectTab(1);
  }
  
  onSelectTab(tab) {
    this.fetchPlans(tab);
  }

  async fetchPlans(active = true) {
    try {
      this.isLoading = true;
      this.subscriptionPlans = [];
      this.subscriptionPlans = await subscriptionPlanService.getPlans(active);

    } catch (e) {
      this.toastError(e);
    } finally {
      this.isLoading = false;
    }
  }

  onClickReload() {
    this.fetchPlans(this.selectedTab);
  }

  async onClickDeletePlan(plan) {
    const modalMessage = this.$t("deleteSubPlanConfirm", { name: plan.name, id: plan.id }).toString();
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.isLoading = true;
        await subscriptionPlanService.deletePackage(plan.id);
        this.toastSuccessDelay(this.$t("deleteSubPlanSuccessful").toString());
      } catch (e) {
        this.toastError(e);
      } finally {
        this.fetchPlans(this.selectedTab);
      }
    }
  }

  //Hàm hiển thị các chức năng đang hoạt động
  //sortedFeatures:Sắp xếp mảng theo featureId tăng dần
  getActiveFeatures(plan) {
    return this.sortedFeatures(plan.features);
  }

  //Hàm hiển thị tooltip các chức năng đang hoạt động
  getFeatureTooltip(plan) {
    const features = this.getActiveFeatures(plan);
    return util.generateFeatureTooltip(features);
  }

  getStorageLabel(plan) {
    if (!plan?.storage) return;      

    const { type, maxSize, retention } = plan.storage || {};
    const sizeGB = bytesToGB(maxSize ?? 0, true);

    // Lấy text dịch, nếu không có thì để trống
    const storageText = this.$t("storage") || "";
    const meetingText = this.$t("meeting") || "";
    const daysText = (this.$t("days") || "").toString().toLowerCase();

    let labelParts = [];

    // Phần chính: storage
    if (sizeGB) {
      labelParts.push(`${storageText}: ${sizeGB}GB`);
    }

    // Nếu là SESSION thì thêm thông tin chi tiết
    if (type === StorageType.Session) {
      if (meetingText) labelParts.push(meetingText);
      if (retention) labelParts.push(`${retention} ${daysText}`);
    }else{

      if (retention) labelParts.push(`${retention} ${daysText}`);
    }

    // Nếu không có phần nào hợp lệ → trả về rỗng
    if (labelParts.length === 0) return "";

    // Ghép các phần lại với " / "
    return `| ${labelParts.join(" / ")}`;
  }

  getQuotaAILabel(plan) {
    if (!plan?.aiQuota) return "";
    return util.getQuotaAILabel(
      plan,
      this.$t("ai_quota").toString(),
      (this.$t("minutesCapital")).toString().toLowerCase(),
      (this.$t("aiQuotaType_Unlimited")).toString(),
      (this.$t("aiQuotaType_NoReset")).toString(),
      (this.$t("aiQuotaType_Fixed")).toString()
    );
  }
}

</script>
